import type { PolicyInspectionApiItem } from '@/services/inspections/types';
import { InspectionStatus } from '@/shared/enums/inspection-status.enum';
import { toaster } from '@/shared/ui/toaster.component';
import {
  getLastInspectionStatusKey,
  isInspectionTerminalStatus,
} from '@/shared/utils';
import {
  Button,
  Dialog,
  Timeline,
} from '@noorden-insurtech/alba-design-system';
import { useNavigate } from '@tanstack/react-router';
import { History, UserRound } from 'lucide-react';
import { useMemo } from 'react';
import { useCreateInspection, usePolicyInspections } from '../hooks';
import { inspectionStatusConfig } from './inspections-table-columns';

interface InspectionHistoryModalProps {
  isOpen: boolean;
  onClose: () => void;
  policyId?: string | null;
  inspectionId?: string | null;
  /** Status da inspeção na listagem (fallback até carregar o detalhe por policy). */
  listingInspectionStatus?: string | null;
}

type TimelineEntry = {
  inspectionId: string;
  day: string;
  month: string;
  time: string;
  title: string;
  status: string;
  description?: string;
  policyId: string;
  responsibleName?: string;
  responsibleId?: string;
  navigable: boolean;
};

function inspectionCreatedAt(
  inspection: PolicyInspectionApiItem
): string | undefined {
  return inspection.createdAt ?? inspection.createDate;
}

function mapPolicyInspectionToTimelineEntry(
  inspection: PolicyInspectionApiItem,
  index: number,
  policyId: string
): TimelineEntry {
  const sequenceLabel = String(index + 1).padStart(3, '0');
  const title = `Inspeção N°${sequenceLabel}`;

  const rawDate = inspectionCreatedAt(inspection);
  const createdAt = rawDate ? new Date(rawDate) : null;

  const day = createdAt ? String(createdAt.getDate()).padStart(2, '0') : '--';
  const month = createdAt
    ? createdAt.toLocaleDateString('pt-BR', { month: 'short' })
    : '--';
  const time = createdAt
    ? createdAt.toLocaleTimeString('pt-BR', {
        hour: '2-digit',
        minute: '2-digit',
      })
    : '--';

  const rawStatus = inspection.statusName;
  const normalizedStatus = rawStatus?.trim().toLowerCase();
  const statusConfig =
    inspectionStatusConfig[
      normalizedStatus as keyof typeof inspectionStatusConfig
    ];
  const statusLabel = statusConfig?.label || rawStatus || '-';

  const inspectionId = inspection.id?.trim() ?? '';

  return {
    inspectionId,
    day,
    month,
    time,
    title,
    status: statusLabel,
    description: inspection.description || undefined,
    policyId: inspection.policyId ?? policyId,
    responsibleName: inspection.responsibleName,
    responsibleId: inspection.responsibleId,
    navigable: Boolean(inspectionId),
  };
}

export function InspectionHistoryModal({
  isOpen,
  onClose,
  policyId,
  inspectionId,
  listingInspectionStatus,
}: InspectionHistoryModalProps) {
  const navigate = useNavigate();
  const { data, isLoading, error } = usePolicyInspections(policyId, {
    enabled: isOpen,
  });

  const createInspection = useCreateInspection();

  const showNewInspectionButton = useMemo(() => {
    if (data?.length) {
      const maxSeq = data.reduce(
        (acc, i) => Math.max(acc, i.sequenceNumber ?? 0),
        0
      );
      const atMaxSeq = data.filter(i => (i.sequenceNumber ?? 0) === maxSeq);
      return (
        atMaxSeq.length > 0 &&
        atMaxSeq.every(i => isInspectionTerminalStatus(i.statusName))
      );
    }

    const raw = listingInspectionStatus?.trim() ?? '';
    const token = getLastInspectionStatusKey(raw) || raw;
    return isInspectionTerminalStatus(token);
  }, [data, listingInspectionStatus]);

  const showGoToDecisionButton = useMemo(() => {
    if (showNewInspectionButton) return true;

    if (data?.length) {
      const maxSeq = data.reduce(
        (acc, i) => Math.max(acc, i.sequenceNumber ?? 0),
        0
      );
      const atMaxSeq = data.filter(i => (i.sequenceNumber ?? 0) === maxSeq);
      return atMaxSeq.some(
        i => i.statusName?.trim().toLowerCase() === InspectionStatus.Dismissed
      );
    }

    const raw = listingInspectionStatus?.trim() ?? '';
    const token = getLastInspectionStatusKey(raw) || raw;
    return token.toLowerCase() === InspectionStatus.Dismissed;
  }, [data, listingInspectionStatus, showNewInspectionButton]);

  const entries = useMemo(() => {
    const items = data ?? [];
    const pid = policyId?.trim() ?? '';
    const sorted = [...items].sort((a, b) => {
      const ta = new Date(inspectionCreatedAt(a) ?? 0).getTime();
      const tb = new Date(inspectionCreatedAt(b) ?? 0).getTime();
      return ta - tb;
    });
    return sorted.map((row, index) =>
      mapPolicyInspectionToTimelineEntry(row, index, pid)
    );
  }, [data, policyId]);

  const handleCardClick = (entry: TimelineEntry) => {
    if (!policyId?.trim() || !entry.navigable) return;
    const entryInspectionId = entry.inspectionId.trim();
    if (!entryInspectionId) return;
    onClose();
    void navigate({
      to: '/policies/$policyId/inspections/$inspectionId',
      params: { policyId, inspectionId: entryInspectionId },
      search: { tab: 'interactions' },
    });
  };

  const handleNewInspection = () => {
    if (!policyId?.trim()) return;
    createInspection.mutate(policyId, {
      onSuccess: result => {
        toaster.create({
          type: 'success',
          title: 'Nova inspeção criada',
          description: 'Redirecionando para a inspeção...',
        });
        onClose();
        void navigate({
          to: '/policies/$policyId/inspections/$inspectionId',
          params: {
            policyId: result.policyId,
            inspectionId: result.inspectionId,
          },
          search: { tab: 'inspection' },
        });
      },
      onError: error => {
        toaster.create({
          type: 'error',
          title: 'Erro ao criar nova inspeção',
          description:
            error instanceof Error ? error.message : 'Erro inesperado',
        });
      },
    });
  };

  const handleGoToDecision = () => {
    if (!policyId?.trim() || !inspectionId?.trim()) return;
    onClose();
    void navigate({
      to: '/policies/$policyId/inspections/$inspectionId',
      params: { policyId, inspectionId },
      search: { tab: 'inspection' },
    });
  };

  return (
    <Dialog.Root open={isOpen} onOpenChange={() => onClose()}>
      <Dialog.Backdrop />
      <Dialog.Positioner>
        <Dialog.Content className='w-[calc(100vw-2.5rem)] max-w-3xl md:w-auto'>
          <Dialog.Header>
            <Dialog.Title>
              <History className='h-6 w-6' />
              Histórico de Inspeções
            </Dialog.Title>
            <Dialog.CloseButton onClick={() => onClose()} />
          </Dialog.Header>

          <Dialog.Body className='max-h-[60vh] overflow-y-auto px-3 sm:px-4'>
            {!policyId?.trim() ? (
              <div className='py-6 text-center text-alba-gray-600'>
                Selecione uma proposta para visualizar o histórico.
              </div>
            ) : isLoading ? (
              <div className='py-6 text-center text-alba-gray-600'>
                Carregando inspeções da proposta...
              </div>
            ) : error ? (
              <div className='py-6 text-center text-alba-error-red-500'>
                Erro ao carregar inspeções da proposta.
              </div>
            ) : entries.length === 0 ? (
              <div className='py-6 text-center text-alba-gray-600'>
                Nenhuma inspeção encontrada para esta proposta.
              </div>
            ) : (
              <Timeline.Root>
                <Timeline.List>
                  {entries.map((entry, index) => (
                    <Timeline.Item
                      key={`${entry.inspectionId || 'no-id'}-${index}`}
                    >
                      <Timeline.Date
                        day={entry.day}
                        month={entry.month}
                        time={entry.time}
                      />
                      <Timeline.Marker />

                      <Timeline.Card
                        size='full'
                        onClick={() => handleCardClick(entry)}
                        className={
                          entry.navigable
                            ? 'cursor-pointer transition-shadow hover:shadow-md'
                            : 'cursor-default opacity-90'
                        }
                      >
                        <Timeline.CardTitle className='text-alba-green-500'>
                          {entry.title}
                        </Timeline.CardTitle>
                        <Timeline.CardDivider />

                        <Timeline.CardBody>
                          <p className='font-montserrat text-sm leading-[150%] text-alba-gray-600'>
                            <span className='font-medium'>Status:</span>{' '}
                            {entry.status}
                          </p>

                          {entry.description && (
                            <p className='font-montserrat text-sm leading-[150%] text-alba-gray-600'>
                              <span className='font-medium'>Descrição:</span>{' '}
                              {entry.description}
                            </p>
                          )}
                        </Timeline.CardBody>

                        {entry.responsibleName && (
                          <Timeline.CardFooter>
                            <UserRound
                              size={16}
                              className='text-alba-gray-600'
                            />
                            <span className='font-montserrat text-sm leading-[150%] text-alba-gray-600'>
                              Responsável: {entry.responsibleName}
                            </span>
                          </Timeline.CardFooter>
                        )}
                      </Timeline.Card>
                    </Timeline.Item>
                  ))}
                </Timeline.List>
              </Timeline.Root>
            )}
          </Dialog.Body>

          <Dialog.Footer>
            {showNewInspectionButton && (
              <Button
                variant='primary'
                size='lg'
                onClick={handleNewInspection}
                disabled={createInspection.isPending}
              >
                {createInspection.isPending ? 'Criando...' : 'Nova Inspeção'}
              </Button>
            )}
            {showGoToDecisionButton && (
              <Button variant='primary' size='lg' onClick={handleGoToDecision}>
                Ir para Decisão
              </Button>
            )}
          </Dialog.Footer>
        </Dialog.Content>
      </Dialog.Positioner>
    </Dialog.Root>
  );
}
